import React from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { partLabel, pointLabel, occupantAt } from '../lib/serializedWork';

const selectClass = 'w-full min-w-0 rounded-md border bg-white px-2 py-2 text-sm';

export const SerializedWorkRow = ({ row, catalog, onChange, onRemove, disabled, plannedOutgoing }) => {
  const part = row.component;
  const prefix = `serial-work-${part.id}`;
  const canInstall = ['new', 'available', 'installed'].includes(part.status);
  const outgoing = row.outgoing;
  const points = catalog.points.filter((p) => p.machinery_id === row.machinery_id);
  const set = (changes) => onChange({ ...row, ...changes });
  const statusSelect = (
    <div className="space-y-1">
      <Label htmlFor={`${prefix}-status`}>Returned part status *</Label>
      <select id={`${prefix}-status`} data-testid={`${prefix}-status`} className={selectClass}
        value={row.outgoing_status || ''} onChange={(e) => set({ outgoing_status: e.target.value })} required>
        <option value="">Choose status…</option>
        <option value="available">Available</option>
        <option value="awaiting_rebuild">Awaiting Rebuild</option>
        <option value="decommissioned">Decommissioned</option>
      </select>
    </div>
  );
  return (
    <fieldset disabled={disabled} className="min-w-0 space-y-3 border rounded-md p-3" data-testid={prefix}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1 break-words">
          <p className="text-sm font-medium" data-testid={`${prefix}-identity`}>{partLabel(part)}</p>
          <p className="text-xs text-slate-600" data-testid={`${prefix}-source`}>
            {part.status.replaceAll('_', ' ')} · {pointLabel(part.current_installation_point_id, catalog)}
          </p>
        </div>
        <Button type="button" variant="ghost" size="icon" className="shrink-0 h-8 w-8" onClick={onRemove}
          aria-label="Remove serialized selection" title="Remove selection" data-testid={`${prefix}-remove`}><X size={16} /></Button>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefix}-action`}>Work on this part</Label>
        <select id={`${prefix}-action`} data-testid={`${prefix}-action`} className={selectClass} value={row.action}
          onChange={(e) => set({ action: e.target.value, installation_point_id: '', outgoing: null, outgoing_action: 'inventory' })}>
          <option value="associate">Link work only — no movement</option>
          {canInstall && <option value="install">Install / replace / transfer</option>}
          {part.status === 'installed' && <option value="remove">Remove to inventory</option>}
        </select>
      </div>
      {row.action === 'install' && <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 min-w-0">
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`${prefix}-equipment`}>Destination equipment *</Label>
            <select id={`${prefix}-equipment`} data-testid={`${prefix}-equipment`} className={selectClass} value={row.machinery_id}
              onChange={(e) => set({ machinery_id: e.target.value, installation_point_id: '', outgoing: null, outgoing_action: 'inventory' })} required>
              <option value="">Choose equipment…</option>
              {catalog.machinery.map((m) => <option key={m.id} value={m.id}>{m.location_name} / {m.component}</option>)}
            </select>
          </div>
          <div className="min-w-0 space-y-1">
            <Label htmlFor={`${prefix}-point`}>Installation point *</Label>
            <select id={`${prefix}-point`} data-testid={`${prefix}-point`} className={selectClass}
              value={row.installation_point_id || ''} required onChange={(e) => set({ installation_point_id: e.target.value,
                outgoing: occupantAt(e.target.value, catalog) || null, outgoing_action: 'inventory', outgoing_status: '' })}>
              <option value="">Choose point…</option>
              {points.filter((p) => p.id !== part.current_installation_point_id).map((p) => {
                const current = occupantAt(p.id, catalog);
                return <option key={p.id} value={p.id}>{p.label} — {current ? `SN ${current.serial_number} (${current.mfr_part_number})` : 'Empty'}</option>;
              })}
            </select>
          </div>
        </div>
        {outgoing && <div className="space-y-2 border-l-2 border-amber-400 pl-3">
          <p className="text-sm break-words" data-testid={`${prefix}-outgoing`}>Removing: {partLabel(outgoing)}</p>
          {plannedOutgoing ? <p className="text-xs text-emerald-800" data-testid={`${prefix}-outgoing-planned`}>Outgoing part moves through its own selected work row.</p> : <>
          <Label htmlFor={`${prefix}-disposition`}>Outgoing part destination</Label>
          <select id={`${prefix}-disposition`} data-testid={`${prefix}-disposition`} className={selectClass}
            value={row.outgoing_action} onChange={(e) => set({ outgoing_action: e.target.value })}>
            <option value="inventory">Return to inventory</option>
            {part.status === 'installed' && <option value="swap">Swap into {pointLabel(part.current_installation_point_id, catalog)}</option>}
          </select>
          {row.outgoing_action === 'inventory' && statusSelect}
          </>}
        </div>}
      </>}
      {row.action === 'remove' && statusSelect}
      <p className="text-xs text-emerald-800 break-words" data-testid={`${prefix}-preview`}>
        {row.action === 'associate' ? 'History linked · placement unchanged' : row.action === 'remove'
          ? `→ Inventory · ${(row.outgoing_status || 'choose status').replaceAll('_', ' ')}`
          : row.installation_point_id ? `→ ${pointLabel(row.installation_point_id, catalog)}${outgoing ? (plannedOutgoing ? ' · outgoing part moves in another row' : row.outgoing_action === 'swap' ? ' · outgoing part returns to donor point' : ` · outgoing part → inventory (${(row.outgoing_status || 'choose status').replaceAll('_', ' ')})`) : ''}` : 'Destination not selected'}
      </p>
    </fieldset>
  );
};